import { Web3ProviderInterface } from '../../types/web3';
import { formatDisplayAddress } from '../../utils/address';
import { Menu, Image, Group, Text, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useTelegram } from '../../providers/TelegramProvider';
import { useGameMasterBackend } from '../../hooks/useGameMasterBackend';
import { IconSettings, IconLogout, IconChevronDown } from '@tabler/icons-react';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { Address } from 'viem';

const UserMenu = ({
  web3ProviderContext,
  userAddress,
}: {
  web3ProviderContext: Web3ProviderInterface;
  userAddress: Address;
}) => {
  const gameMasterService = useGameMasterBackend();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const router = useRouter();
  const { user } = usePrivy();
  const { telegramWebApp } = useTelegram();
  const theme = useMantineTheme();
  const isXsScreen = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

  const userProfileQuery = gameMasterService.useUserProfile({ userAddress });

  const showLogoutButton = telegramWebApp === undefined;

  // Get the best display name prioritizing social accounts (matching settings.tsx supported accounts)
  const getDisplayName = () => {
    // Priority 0: Stored username from database
    if (userProfileQuery.data?.username) {
      return userProfileQuery.data.username;
    }

    // Priority 1: GitHub username (most professional/recognizable)
    if (user?.github?.username) {
      return user.github.username;
    }

    // Priority 2: Discord username
    if (user?.discord?.username) {
      return user.discord.username;
    }

    // Priority 3: Telegram username
    if (user?.telegram?.username) {
      return `@${user.telegram.username}`;
    }

    // Priority 4: Spotify email/name (for music-focused app)
    if (user?.spotify?.email) {
      return user.spotify.email.split('@')[0];
    }

    // Priority 5: Google name or email (first part)
    if (user?.google?.name) {
      return user.google.name;
    }
    if (user?.google?.email) {
      return user.google.email.split('@')[0];
    }

    // Priority 6: Farcaster username (if available)
    if (user?.farcaster?.username) {
      return user.farcaster.username;
    }

    // Priority 7: Email address (first part)
    if (user?.email?.address) {
      return user.email.address.split('@')[0];
    }

    // Priority 9: Fallback to formatted wallet address
    return formatDisplayAddress(web3ProviderContext.account);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await web3ProviderContext.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <Menu shadow="md" opened={menuOpened} onChange={setMenuOpened} position="bottom-end" offset={8}>
      <Menu.Target>
        <Group
          gap={8}
          px={12}
          py={8}
          style={{
            cursor: 'pointer',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            transition: 'all 0.2s ease-in-out',
            backdropFilter: 'blur(10px)',
          }}
          styles={{
            root: {
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              },
              '&:active': {
                transform: 'translateY(0px)',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            },
          }}
        >
          <Image
            display="inline"
            h="20px"
            w="20px"
            src="/images/eth-diamond-rainbow.png"
            alt=""
            style={{ flexShrink: 0 }}
          />
          <Text
            component="span"
            size="sm"
            fw={500}
            style={{
              color: 'rgba(255, 255, 255, 0.9)',
              letterSpacing: '0.5px',
              maxWidth: isXsScreen ? '130px' : '100%',
            }}
          >
            {getDisplayName()}
          </Text>
          <IconChevronDown
            size={16}
            style={{
              color: 'rgba(255, 255, 255, 0.6)',
              transition: 'transform 0.2s ease-in-out',
              flexShrink: 0,
              transform: menuOpened ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Group>
      </Menu.Target>
      {showLogoutButton && (
        <Menu.Dropdown
          style={{
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            backdropFilter: 'blur(20px)',
            borderRadius: '8px',
            minWidth: '180px',
          }}
        >
          <Menu.Item
            rightSection={<IconSettings size={18} />}
            onClick={() => router.push('/settings')}
            style={{
              borderRadius: '6px',
              margin: '4px',
            }}
          >
            Settings
          </Menu.Item>
          <Menu.Divider style={{ borderColor: 'rgba(255, 255, 255, 0.1)', margin: '4px 8px' }} />
          <Menu.Item
            rightSection={<IconLogout size={18} />}
            onClick={handleLogout}
            disabled={isLoggingOut}
            style={{
              borderRadius: '6px',
              margin: '4px',
              opacity: isLoggingOut ? 0.6 : 1,
            }}
          >
            {isLoggingOut ? 'Logging out...' : 'Logout'}
          </Menu.Item>
        </Menu.Dropdown>
      )}
    </Menu>
  );
};

export default UserMenu;
